import loadable from '@loadable/component';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { FairValueProvider } from '../../../../FairValue/Context';
import { FairValueContainer } from '../../../../FairValue/Container';

import { useIsMobileScreen } from '@/hooks/useMedia';
import { useWatchlist } from '@/hooks/useWatchlist';
import type { WatchlistItem } from '@/hooks/useWatchlist';
import { useSortedWatchlistItems } from '../../../../../hooks/useWatchlistSorting';

import { Table, StyledTable } from '../../Table';

const WatchlistRowDesktop = loadable(() => import('../../WatchlistTableRow'), {
  resolveComponent: (c) => c.WatchlistRowDesktop,
});
const WatchlistRowExpanded = loadable(() => import('../../WatchlistTableRow'), {
  resolveComponent: (c) => c.WatchlistRowExpanded,
});
const WatchlistRowSkeleton = loadable(() => import('../../WatchlistTableRow'), {
  resolveComponent: (c) => c.WatchlistRowSkeleton,
});

type Props = {
  watchlistItemId?: WatchlistItem['id'] | null;
  onWatchlistItemSelect: (id: WatchlistItem['id'] | null) => void;
};

const WatchlistTable = ({ watchlistItemId, onWatchlistItemSelect }: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const { data, status } = useWatchlist();
  const items = useSortedWatchlistItems(data?.items);

  const isLoading = status === 'pending';
  const hasData = status === 'success' && typeof data !== 'undefined';

  return isLoading ? (
    <StyledTable>
      <WatchlistRowSkeleton />
      <WatchlistRowSkeleton />
      <WatchlistRowSkeleton />
      <WatchlistRowSkeleton />
      <WatchlistRowSkeleton />
    </StyledTable>
  ) : hasData ? (
    <Table watchlistItemId={watchlistItemId}>
      <FairValueProvider>
        {items.map((item, index) =>
          !isMobileScreen && watchlistItemId ? (
            <WatchlistRowDesktop
              key={item.id}
              selected={watchlistItemId === item.id}
              index={index}
              watchlistItem={item}
              watchlistId={data.id}
              onWatchlistItemSelect={onWatchlistItemSelect}
              data-cy-id="watchlist-table-row"
            />
          ) : (
            <WatchlistRowExpanded
              key={item.id}
              index={index}
              watchlistItem={item}
              watchlistId={data.id}
              onWatchlistItemSelect={onWatchlistItemSelect}
              data-cy-id="watchlist-table-row"
            />
          )
        )}
        <FairValueContainer />
      </FairValueProvider>
    </Table>
  ) : null;
};

export default WatchlistTable;
